import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useLoaderData } from 'react-router-dom'
import { XIcon } from 'lucide-react'

import type { PreJoinLoaderData } from '@/app/loaders/prejoinLoader'
import { AppTemplate } from '@/components/templates/AppTemplate/AppTemplate'
import { MeetingHeader } from '@/components/molecules/Header/MeetingHeader'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/Avatar/Avatar'
import { DeviceSelector } from '@/components/molecules/DeviceSelector/DeviceSelector'
import { ROUTES } from '@/constants/routes'
import { PeripheralToggleButton } from '@/components/atoms/PeripheralToggleButton'
import { MeetingActionButton } from '@/components/molecules/MeetingActionButton/MeetingActionButton'
import { usePhoneRingtones } from '@/hooks/usePhoneRingtones'
import { useJoinMeeting } from '@/hooks/useJoinMeeting'
import { useBrowserDevices } from '@/hooks/useBrowserDevices'

export function JoinPhoneMeetingPage() {
  const { id: appointmentId } = useParams<{ id: string }>()
  const normalizedAppointmentId = appointmentId?.trim() ?? ''
  const { appointment: appointmentData } = useLoaderData() as PreJoinLoaderData

  const navigate = useNavigate()
  const cancelledRef = useRef(false)
  const hasTriggeredJoinRef = useRef(false)
  const deviceIdsRef = useRef<{ audioInputId?: string; audioOutputId?: string }>({})

  const { playRingtones, stopRingtones } = usePhoneRingtones()
  const { join, isJoining, error } = useJoinMeeting(normalizedAppointmentId)
  const {
    audioInputs,
    audioOutputs,
    selectedAudioInput,
    selectedAudioOutput,
    selectAudioInput,
    selectAudioOutput
  } = useBrowserDevices()

  deviceIdsRef.current = { audioInputId: selectedAudioInput, audioOutputId: selectedAudioOutput }

  const [isMuted, setIsMuted] = useState(false)

  const handleHangUp = useCallback(() => {
    cancelledRef.current = true
    stopRingtones(deviceIdsRef.current.audioOutputId)
    navigate(ROUTES.phoneWaitingRoom(normalizedAppointmentId))
  }, [normalizedAppointmentId, navigate, stopRingtones])

  useEffect(() => {
    if (!normalizedAppointmentId) return

    cancelledRef.current = false
    hasTriggeredJoinRef.current = false

    const doJoin = () => {
      if (cancelledRef.current || hasTriggeredJoinRef.current) return
      hasTriggeredJoinRef.current = true
      const { audioInputId, audioOutputId } = deviceIdsRef.current
      join({
        options: {
          mode: 'audio',
          startVideo: false,
          audioInputId,
          audioOutputId
        }
      }).catch(() => {
        // Error already set in useJoinMeeting; user can hang up to leave
      })
    }

    if (appointmentData.hasInterlocutorJoined) {
      playRingtones(deviceIdsRef.current.audioOutputId).then(doJoin)
    } else {
      doJoin()
    }
  }, [normalizedAppointmentId, join, playRingtones, appointmentData.hasInterlocutorJoined])

  const participant = appointmentData?.interlocutor
  const statusMessage = isJoining ? 'Connexion à l\'appel…' : 'Appel en cours...'

  if (!participant) {
    return null
  }

  return (
    <AppTemplate header={<MeetingHeader centered />} className="bg-primary text-white">
      <div className="flex flex-col justify-between h-[calc(100dvh-32px)] py-12 w-full px-4">
        <div className="flex flex-col items-center px-4 gap-y-1 md:mt-[12dvh]">
          <p className="text-xl font-medium">
            {participant.firstName} {participant.lastName}
          </p>
          <p className="text-xl text-gray-300">{statusMessage}</p>
          <Avatar className="h-40 w-40 mt-12">
            {participant.avatar ? (
              <AvatarImage src={participant.avatar} />
            ) : (
              <AvatarFallback className="text-5xl text-primary">{participant.firstName?.charAt(0) ?? '?'}</AvatarFallback>
            )}
          </Avatar>
          {error && (
            <p className="mt-4 text-sm text-red-200 text-center" role="alert">
              {error.message}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-y-6 w-full">
          <div className="flex items-center justify-center gap-x-10 mb-8">
            <DeviceSelector
              selectedAudioInput={selectedAudioInput}
              selectedAudioOutput={selectedAudioOutput}
              audioInputs={audioInputs}
              audioOutputs={audioOutputs}
              onAudioInputChange={(id) => void selectAudioInput({ id })}
              onAudioOutputChange={(id) => void selectAudioOutput({ id })}
              buttonSize="lg"
            />

            <PeripheralToggleButton
              icon="microphone"
              isActive={isMuted}
              onClick={() => setIsMuted((m) => !m)}
              aria-label="Couper le micro"
              buttonSize="lg"
            />

            <MeetingActionButton icon={XIcon} onClick={handleHangUp} variant="destructive" buttonSize="lg" />
          </div>
        </div>
      </div>
    </AppTemplate>
  )
}
